<template>
  <div class="leave-manage">
    <van-nav-bar title="请假管理" left-arrow @click-left="goBack" fixed placeholder />

    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <van-list
        v-model:loading="loading"
        :finished="finished"
        finished-text="没有更多了"
        @load="loadData"
        :immediate-check="false"
      >
        <div v-if="list.length > 0" class="list-wrap">
          <div class="leave-card" v-for="item in list" :key="item.id">
            <div class="card-header">
              <div class="stu-info">
                <span class="stu-name">{{ item.student_name }}</span>
                <span class="class-name">{{ item.class_name }}</span>
              </div>
              <van-tag :type="leaveStatusType(item.status)" plain size="medium">
                {{ leaveStatusText(item.status) }}
              </van-tag>
            </div>
            <div class="card-body">
              <div class="info-row dates-row">
                <span class="label">请假日期：</span>
                <div class="dates-tags">
                  <span class="date-tag" v-for="d in parseDates(item.dates)" :key="d">{{
                    d
                  }}</span>
                  <span class="date-count" v-if="parseDates(item.dates).length">
                    共{{ parseDates(item.dates).length }}天
                  </span>
                </div>
              </div>
              <div class="info-row">
                <span class="label">请假原因：</span>
                <span>{{ item.content || "-" }}</span>
              </div>
              <div class="info-row time">
                <span>提交时间：{{ item.create_at }}</span>
              </div>
            </div>
            <!-- 待审核才显示操作按钮 -->
            <div class="card-actions" v-if="item.status == 1">
              <van-button size="small" type="danger" plain @click="handleReject(item)"
                >拒绝</van-button
              >
              <van-button size="small" type="primary" @click="handleApprove(item)"
                >通过</van-button
              >
            </div>
            <!-- 已通过(status=2)显示取消请假 -->
            <div class="card-actions" v-else-if="item.status == 2">
              <van-button size="small" type="danger" plain @click="handleCancel(item)"
                >取消请假</van-button
              >
            </div>
          </div>
        </div>
        <van-empty v-else-if="!loading" description="暂无请假申请" />
      </van-list>
    </van-pull-refresh>

    <!-- 拒绝原因弹窗 -->
    <van-dialog
      v-model:show="rejectDialogVisible"
      title="拒绝原因"
      show-cancel-button
      confirm-button-text="提交"
      cancel-button-text="取消"
      :before-close="onRejectConfirm"
    >
      <van-field
        v-model="rejectReason"
        rows="3"
        autosize
        type="textarea"
        maxlength="20"
        placeholder="请输入拒绝原因"
        show-word-limit
        style="padding: 12px 16px"
      />
    </van-dialog>

    <!-- 取消请假确认弹窗 -->
    <van-dialog
      v-model:show="cancelDialogVisible"
      title="取消请假"
      show-cancel-button
      confirm-button-text="确定取消"
      cancel-button-text="取消"
      :before-close="onCancelConfirm"
    >
      <div style="padding: 20px 16px; text-align: center; font-size: 14px; color: #666">
        确定取消 {{ cancelItem ? cancelItem.student_name : "" }} 的请假吗？
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";
import { leaveStatusText, leaveStatusType } from "../utils/leaveStatus";

export default {
  setup() {
    const router = useRouter();
    const list = ref([]);
    const loading = ref(false);
    const finished = ref(false);
    const refreshing = ref(false);
    const page = ref(1);
    const rejectDialogVisible = ref(false);
    const rejectReason = ref("");
    const rejectItem = ref(null);
    const cancelDialogVisible = ref(false);
    const cancelItem = ref(null);

    // 解析日期字符串为数组
    const parseDates = (dates) => {
      if (!dates) return [];
      return dates
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
    };

    // 状态文案/标签类型见 ../utils/leaveStatus（共用，避免三处漂移）

    // 加载数据
    const loadData = async () => {
      try {
        const res = await api.getLeaveListByTeacherId({
          sid: localStorage.getItem("sid"),
          user_id: sessionStorage.getItem("userId"),
          page: page.value,
        });
        loading.value = false;
        if (res.data.code == 1001) {
          const result = res.data.result;
          if (refreshing.value) {
            list.value = [];
            refreshing.value = false;
          }
          list.value = list.value.concat(result.data || []);
          if (result.current_page >= result.last_page) {
            finished.value = true;
          } else {
            page.value++;
          }
        } else {
          finished.value = true;
          Toast(res.data.message || "加载失败");
        }
      } catch (error) {
        loading.value = false;
        refreshing.value = false;
        Toast("加载失败");
      }
    };

    // 下拉刷新
    const onRefresh = () => {
      page.value = 1;
      finished.value = false;
      loading.value = true;
      loadData();
    };

    // 通过
    const handleApprove = (item) => {
      Dialog.alert({
        title: "确认操作",
        message: "确定通过" + item.student_name + "的请假申请吗？",
        showCancelButton: true,
      })
        .then(async () => {
          try {
            Toast.loading({ message: "处理中...", forbidClick: true });
            const res = await api.recordLeave({
              user_id: sessionStorage.getItem("userId"),
              student_leave_id: item.id,
              content: "",
              status: 2,
            });
            Toast.clear();
            if (res.data.code == 1001) {
              item.status = 2;
              Toast.success("通过成功");
            } else {
              Toast(res.data.message || "操作失败");
            }
          } catch (error) {
            Toast.clear();
            Toast("操作失败，请重试");
          }
        })
        .catch(() => {});
    };

    // 取消请假（已通过的记录）：复用 recordLeave，status=3=取消
    const handleCancel = (item) => {
      cancelItem.value = item;
      cancelDialogVisible.value = true;
    };

    // 取消请假弹窗确认
    const onCancelConfirm = async (action) => {
      if (action === "confirm") {
        try {
          Toast.loading({ message: "处理中...", forbidClick: true });
          const res = await api.recordLeave({
            user_id: sessionStorage.getItem("userId"),
            student_leave_id: cancelItem.value.id,
            content: "",
            status: 3,
          });
          Toast.clear();
          if (res.data.code == 1001) {
            cancelItem.value.status = 3;
            Toast.success("已取消请假");
            cancelItem.value = null;
            return true;
          } else {
            Toast(res.data.message || "操作失败");
            return false;
          }
        } catch (error) {
          Toast.clear();
          Toast("操作失败，请重试");
          return false;
        }
      }
      return true;
    };

    // 拒绝（弹出输入原因弹窗）
    const handleReject = (item) => {
      rejectItem.value = item;
      rejectReason.value = "";
      rejectDialogVisible.value = true;
    };

    // 拒绝弹窗确认
    const onRejectConfirm = async (action) => {
      if (action === "confirm") {
        if (!rejectReason.value.trim()) {
          Toast("请输入拒绝原因");
          return false; // 阻止关闭
        }
        try {
          Toast.loading({ message: "处理中...", forbidClick: true });
          const res = await api.recordLeave({
            user_id: sessionStorage.getItem("userId"),
            student_leave_id: rejectItem.value.id,
            content: rejectReason.value,
            status: 3,
          });
          Toast.clear();
          if (res.data.code == 1001) {
            rejectItem.value.status = 3;
            Toast.success("拒绝成功");
            rejectItem.value = null;
            return true;
          } else {
            Toast(res.data.message || "操作失败");
            return false;
          }
        } catch (error) {
          Toast.clear();
          Toast("操作失败，请重试");
          return false;
        }
      }
      return true;
    };

    const goBack = () => router.back();

    onMounted(() => {
      loading.value = true;
      loadData();
    });

    return {
      list,
      loading,
      finished,
      refreshing,
      parseDates,
      leaveStatusText,
      leaveStatusType,
      loadData,
      onRefresh,
      handleApprove,
      handleReject,
      handleCancel,
      cancelDialogVisible,
      cancelItem,
      onCancelConfirm,
      rejectDialogVisible,
      rejectReason,
      onRejectConfirm,
      goBack,
    };
  },
};
</script>

<style lang="less" scoped>
.leave-manage {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 20px;
}

.list-wrap {
  padding: 10px;
}

.leave-card {
  background: #fff;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f5f5f5;
}

.stu-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stu-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.class-name {
  font-size: 12px;
  color: #999;
}

.card-body {
  font-size: 13px;
  color: #666;
}

.info-row {
  margin-bottom: 6px;

  .label {
    color: #999;
  }
}

.info-row.time {
  color: #bbb;
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: 0;
}

.info-row.dates-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;

  .label {
    flex-shrink: 0;
    line-height: 24px;
  }
}

.dates-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  flex: 1;
}

.date-tag {
  display: inline-block;
  padding: 3px 10px;
  background: var(--color-primary-light);
  color: var(--color-primary);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.date-count {
  font-size: 12px;
  color: #999;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f5f5f5;
}
</style>
